/* eslint-disable react-hooks/exhaustive-deps */
/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *==================================================
 */
import { Grid, Typography } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pagination } from "../components";
import PaginationPageSize from "../components/pagination-page-size";
import { PAGINATION_PAGE_SIZES } from "../constants/common";
import { useInventoryContext } from "../context/inventory-context";
import { useInventoryHandler } from "../hooks/rest/use-inventory-handler";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#E0E0E0",
    color: theme.palette.common.black,
    fontWeight: "bold",
  },
  body: {
    fontSize: 14,
    border: "solid 1px black",
  },
}))(TableCell);

const useStyles = makeStyles({
  table: {
    minWidth: 700,
  },
});

export interface InventoryResults {
  sku: string;
  quantity: number;
  sucursal: string;
  storeName: string;
  ffmCenterName: string;
  productName: string;
}

export interface InventoryResponseData {
  status: number;
  total: number;
  count: number;
  page: number;
  data: InventoryResults[];
  prevPage: string;
  nextPage: string;
  path: string;
  shouldPaginate: boolean;
}

type OrderSearchResultsProps = {};

enum PrevNext {
  PREV = "prev",
  NEXT = "next",
}

const InventorySearchResults: FC<OrderSearchResultsProps> = () => {
  const { t } = useTranslation();
  const {
    selectedStore,
    search,
    selectedFfmCenter,
    sku,
    setIsResultsLoading,
    setSearch,
  } = useInventoryContext();
  const { getInventory, getPreviousInventory, getNextInventory } =
    useInventoryHandler();
  const [inventoryResponseData, setInventoryResponseData] =
    useState<InventoryResponseData | null>(null);
  const [pageSizeValue, setPageSizeValue] = useState(PAGINATION_PAGE_SIZES[0]);
  const [page, setPage] = useState(1);
  const allResults = useRef<InventoryResults[]>([]);
  const [inventoryResults, setInventoryResults] = useState<InventoryResults[]>(
    []
  );

  const classes = useStyles();
  const cancels: any = { cancels: [] };

  const handlePrevNext = async (action: PrevNext) => {
    setIsResultsLoading(true);
    if (action === PrevNext.PREV) {
      const [res, canceler1] = getPreviousInventory(
        inventoryResponseData?.prevPage || ""
      );
      cancels.cancels = canceler1;

      try {
        const r = await res;
        const data = r.data;
        setInventoryResponseData(data);
        setInventoryResults(data.data);
      } catch (error) {
        console.log("Error fetching inventory", error);
      }
    } else if (action === PrevNext.NEXT) {
      const [res, canceler1] = getNextInventory(
        inventoryResponseData?.nextPage || ""
      );
      cancels.cancels = canceler1;

      try {
        const r = await res;
        const data = r.data;
        setInventoryResponseData(data);
        setInventoryResults(data.data);
      } catch (error) {
        console.log("Error fetching inventory", error);
      }
    }
    setIsResultsLoading(false);
  };

  const fetchInventory = async (
    pageSize: number,
    ffmCenterId?: number,
    sku?: string
  ) => {
    if (!selectedStore) {
      return;
    }
    const [res, canceler1] = getInventory(
      selectedStore.storeId,
      pageSize,
      ffmCenterId,
      sku
    );
    cancels.cancels = canceler1;

    try {
      const r = await res;
      const data: InventoryResponseData = r.data;
      setInventoryResponseData(data);
      allResults.current = data.data;
      setInventoryResults(allResults.current.slice(0, pageSize));
    } catch (error) {
      console.log("Error fetching inventory", error);
    } finally {
      setIsResultsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStore) {
      if (inventoryResponseData?.shouldPaginate) {
        setIsResultsLoading(true);
        fetchInventory(
          Number(pageSizeValue),
          selectedFfmCenter?.fulfillmentCenterId,
          sku
        );
      } else {
        const results = allResults.current.slice(
          (page - 1) * pageSizeValue,
          page * pageSizeValue
        );
        setInventoryResults(results);
      }
    }
  }, [page, pageSizeValue]);

  const handlePageSizeChange = (e: any) => {
    setPageSizeValue(e.target.value);
  };

  useEffect(() => {
    if (search) {
      setIsResultsLoading(true);
      fetchInventory(
        pageSizeValue,
        selectedFfmCenter?.fulfillmentCenterId,
        sku
      );
      setSearch(false);
    }
  }, [search]);

  return (
    <>
      <Grid container direction="row" alignItems="stretch">
        <Grid item xs={12}>
          <Typography variant="h2">
            {t("OrderManagement.SearchResultTitle")}
          </Typography>
        </Grid>

        <Grid
          container
          direction="column"
          spacing={2}
          style={{ marginTop: "20px", padding: "10px" }}>
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <StyledTableCell align="center">TIENDA</StyledTableCell>
                  <StyledTableCell align="center">BODEGA</StyledTableCell>
                  <StyledTableCell align="center">SKU</StyledTableCell>
                  <StyledTableCell align="center">PRODUCTO</StyledTableCell>
                  <StyledTableCell align="center">CANTIDAD</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryResults?.map((row) => (
                  <TableRow key={`${row.sku}-${row.sucursal}-${row.quantity}`}>
                    <TableCell align="center">{row.storeName}</TableCell>
                    <TableCell align="center">
                      {row.ffmCenterName || "-"}
                    </TableCell>
                    <TableCell align="center">{row.sku}</TableCell>
                    <TableCell align="center">
                      {row.productName || "-"}
                    </TableCell>
                    <TableCell align="center">{row.quantity}</TableCell>
                  </TableRow>
                ))}
                {!!inventoryResponseData && inventoryResults?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="h6">
                        La búsqueda no ha devuelto ningún resultado
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {!!inventoryResponseData && inventoryResults.length > 0 && (
          <Grid container justify="flex-end" alignItems="center" spacing={3}>
            <PaginationPageSize
              pageSizeValue={pageSizeValue}
              handlePageSizeChange={handlePageSizeChange}
            />

            <Grid item>
              {inventoryResponseData.shouldPaginate && (
                <Pagination
                  getPrev={() => handlePrevNext(PrevNext.PREV)}
                  getNext={() => handlePrevNext(PrevNext.NEXT)}
                  offset={
                    (inventoryResponseData.page - 1) *
                    inventoryResponseData.count
                  }
                  limit={inventoryResponseData.count}
                  count={inventoryResponseData.total}
                />
              )}
              {!inventoryResponseData.shouldPaginate && (
                <Pagination
                  getPrev={() => {
                    setPage(page - 1);
                  }}
                  getNext={() => {
                    setPage(page + 1);
                  }}
                  offset={(page - 1) * pageSizeValue}
                  limit={pageSizeValue}
                  count={inventoryResponseData.total}
                />
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default InventorySearchResults;
